import {
    Stack,
    Text,
    Loader,
    Title,
    Group as MGroup,
    ActionIcon,
} from "@mantine/core";
import slugify from "@sindresorhus/slugify";
import IconDotsVertical from "../../assets/dots-vertical.svg?react";
import classes from "./HistoryPage.module.css";
import {useTranslation} from "react-i18next";
import {
    useGetApiHistoryQuery,
    ItemDto,
} from "../../dibAdminApiCodegen/dibadminApi";
import {isToday, subDays} from "date-fns";
import {useMemo} from "react";
import {TOPIC_TYPE_PATHS} from "../content/paths";
import {ExperimentalLink} from "../../components/ExperimentalLink";

type Group = {
    label: string;
    items: ItemDto[];
    showLabel: boolean;
};

function isTopicTypePathKey(key: number): key is keyof typeof TOPIC_TYPE_PATHS {
    return key in TOPIC_TYPE_PATHS;
}

function getUrlForItem(item: ItemDto) {
    const key = parseInt(item.type ?? "", 10);
    if (isTopicTypePathKey(key)) {
        const topicTypePath = TOPIC_TYPE_PATHS[key];
        const topicPart = `/${topicTypePath}/${slugify(item.name ?? "t")}/${encodeURIComponent(item.id?.toLowerCase() ?? "")}`;

        return `${topicPart}`;
    } else {
        return "";
    }
}

export function HistoryPage() {
    const {data, isLoading} = useGetApiHistoryQuery();
    const {t} = useTranslation();

    const groups = useMemo(() => {
        const items = [...(data?.items ?? [])];
        if (items.length === 0) return [];

        items.sort((a, b) => {
            const da = a.date ? new Date(a.date).getTime() : 0;
            const db = b.date ? new Date(b.date).getTime() : 0;
            return db - da;
        });

        const sevenDaysAgo = subDays(new Date(), 7);
        const today: ItemDto[] = [];
        const lastWeek: ItemDto[] = [];
        const previous: ItemDto[] = [];

        for (const item of items) {
            const date = item.date ? new Date(item.date) : null;
            if (date && isToday(date)) {
                today.push(item);
            } else if (date && date >= sevenDaysAgo) {
                lastWeek.push(item);
            } else {
                previous.push(item);
            }
        }

        const hasRecentGroup = today.length > 0 || lastWeek.length > 0;
        const result: Group[] = [];
        if (today.length > 0)
            result.push({
                label: t("date.today"),
                items: today,
                showLabel: true,
            });
        if (lastWeek.length > 0)
            result.push({
                label: t("date.lastWeek"),
                items: lastWeek,
                showLabel: true,
            });
        if (previous.length > 0)
            result.push({
                label: t("date.previously"),
                items: previous,
                showLabel: hasRecentGroup,
            });
        return result;
    }, [data?.items, t]);

    if (isLoading) {
        return (
            <Stack align="center">
                <Loader />
            </Stack>
        );
    }

    if (groups.length === 0) {
        return (
            <Stack>
                <Text c="dimmed">No history found.</Text>
            </Stack>
        );
    }

    return (
        <Stack gap={0}>
            <Title order={3} mb="md" size={"lg"} className={classes.title}>
                {t("Widget.internal.portal.history")}
            </Title>
            {groups.map((group) => (
                <Stack key={group.label} gap={0} className={classes.groupStack}>
                    {group.showLabel && (
                        <Text className={classes.groupLabel}>
                            {group.label}
                        </Text>
                    )}
                    {group.items.map((item, i) => (
                        <MGroup
                            justify="space-between"
                            wrap="nowrap"
                            className={classes.itemRow}
                            key={`history-${item.id ?? i}`}
                        >
                            <ExperimentalLink
                                href={getUrlForItem(item)}
                                className={classes.itemLink}
                            >
                                <Text className={classes.itemName}>
                                    {item.name}
                                </Text>
                                {item.description && (
                                    <Text className={classes.itemDescription}>
                                        {item.description}
                                    </Text>
                                )}
                            </ExperimentalLink>
                            <ActionIcon variant="subtle" color="gray" size="sm">
                                <IconDotsVertical width={16} height={16} />
                            </ActionIcon>
                        </MGroup>
                    ))}
                </Stack>
            ))}
        </Stack>
    );
}
